import React, { useContext, useEffect, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';

import ProdutoDafIncorporacoes from './components/ProdutoIncorporacoes';
import ProdutoKits from './components/ProdutoKits';
import ProdutoOutrosSistemas from './components/ProdutoOutrosSistemas';
import OffcanvasTagsProduto from './components/ProdutoTags';
import { useProdutoTags } from './hooks/useProdutoTags';
import SubAbasProdutosDaf from './subabas';
import { useStatusStyles } from './utils/constStyles';
import { statusBin, statusBinAtivoInativo } from './utils/enums';
import { defaultProdutoDafFormMain } from './utils/formProdutoDafDefault';
import { produtoDafFormToApi } from './utils/produtoDafMapper';
import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../../components/BarraSubAbas';
import BoxConteudo from '../../../../../components/BoxConteudo';
import ContainerConteudo from '../../../../../components/ContainerConteudo';
import CustomCol from '../../../../../components/CustomCol';
import CustomInputDate from '../../../../../components/CustomInputs/CustomInputDate';
import CustomInputText from '../../../../../components/CustomInputs/CustomInputText';
import CustomRow from '../../../../../components/CustomRow';
import CustomSelect from '../../../../../components/CustomSelects/CustomSelect';
import StyledLink from '../../../../../components/Link';
import Log from '../../../../../components/Log/Log';
import ReadonlyInputWithEdit from '../../../../../components/ReadOnlyWithEdit';
import SubAbas from '../../../../../components/SubAbas';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import { getUser } from '../../../../../modules/contexts/Auth/RequireAuth';
import { UserContext } from '../../../../../modules/contexts/Auth/UserProvider';
import { useSelect } from '../../../../../modules/contexts/Select/useSelect';
import {
  IProdutoDafForm,
  IProdutoDafRegistro,
} from '../../../../../modules/types/ProdutoDaf';
import { useDenominacoesGenericasCache } from '../../../../../services/cache/useDenominacoesGenericasCache';
import { useProdutoVmpCache } from '../../../../../services/cache/useProdutoVmpCache';
import { clearProdutosDafMemoryCache } from '../../../../../services/cache/useProdutosDafCache';
import { useAcondicionamento } from '../../../../../services/enum/useAcondicionamento';
import { useClassificacaoAware } from '../../../../../services/enum/useClassificacaoAware';
import { useFormaFarmaceutica } from '../../../../../services/enum/useFormaFarmaceutica';
import { useGrupoFinanciamento } from '../../../../../services/enum/useGrupoFinanciamento';
import { useListaPaciente } from '../../../../../services/enum/useListaPaciente';
import { useStatusRename } from '../../../../../services/enum/useStatusRename';
import { useTipoProduto } from '../../../../../services/enum/useTipoProduto';
import { useUmidadeProduto } from '../../../../../services/enum/useUmidadeProduto';
import { getProdutoDAF } from '../../../../../services/produtosDAF/produtosDafService';
import { functionLogData } from '../../../../../utils/functionLogData';
import { handleDelete } from '../../../../../utils/handleDelete';
import { handleSave } from '../../../../../utils/handleSave';

type ProdutoOption = {
  value: number;
  label: string;
};

export default function ProdutosDafDadosGerais() {
  //COMPONENTE
  const params = useParams();
  const fichaID = String(params.id);

  //DADOS USUARIO
  const user = getUser();
  const userID = user.sub;
  const { cudProdutoDaf } = useContext(UserContext);

  //Options
  const { getSelectDominioPai } = useSelect();
  const grupoOncologico = getSelectDominioPai(149);
  const tipoProduto = useTipoProduto();
  const formaFarmaceutica = useFormaFarmaceutica();
  const acondicionamento = useAcondicionamento();
  const umidade = useUmidadeProduto();
  const classificacaoAware = useClassificacaoAware();
  const statusRename = useStatusRename();
  const grupoFinanciamento = useGrupoFinanciamento();
  const listaPaciente = useListaPaciente();

  const {
    getStatusDistribuicaoStyle,
    getStatusBinStyle,
    getStatusRenameStyle,
  } = useStatusStyles();

  // Gatilho para atualização do Log
  const [refreshTriggerLog, setRefreshTriggerLog] = useState(0);
  const triggerRefresh = React.useCallback(() => {
    setRefreshTriggerLog((prev) => prev + 1);
  }, []);

  const [logData, setLogData] = useState<IProdutoDafRegistro | null>(null);

  // FORMULÁRIO
  const [dataRaw, setDataRaw] = useState<IProdutoDafForm | null>(null);
  const [formMain, setFormMain] = useState<IProdutoDafForm>(
    defaultProdutoDafFormMain,
  );

  //Buscar dados
  const snToNumber = (value?: string): number => (value === 'S' ? 1 : 2);

  const fetchData = async (id: string | number): Promise<void> => {
    try {
      const registroData = await getProdutoDAF(String(id));

      setLogData(registroData);

      const formatDate = (value?: string | null) =>
        value ? value.split('T')[0] : '';

      const formValues: IProdutoDafForm = {
        id: registroData.id,
        obmVmpId: registroData.obmVmp?.id ?? registroData.obmVmp ?? 0,
        denominacaoGenericaId:
          registroData.denominacaoGenerica?.id ??
          registroData.denominacaoGenericaId ??
          0,
        denominacaoGenerica: registroData.denominacaoGenerica,

        aware: registroData.aware,
        formaFarmaceutica: registroData.formaFarmaceutica,
        formaFarmaceuticaSimp: registroData.formaFarmaceuticaSimp,
        umidade: registroData.umidade,
        acondicionamento: registroData.acondicionamento,
        statusRename: registroData.statusRename,
        listaPaciente: registroData.listaPaciente,
        especializadoGrupo: registroData.especializadoGrupo,
        oncologicoGrupo: registroData.oncologicoGrupo,

        produtoFarmaceutico: registroData.produtoFarmaceutico,
        concentracao: registroData.concentracao,
        codigoAtc: registroData.codigoAtc,

        oncologico: snToNumber(registroData.oncologico),
        biologico: snToNumber(registroData.biologico),
        doencaRara: snToNumber(registroData.doencaRara),
        statusDistribuicao: snToNumber(registroData.statusDistribuicao),

        compBasico: snToNumber(registroData.compBasico),
        compEspecializado: snToNumber(registroData.compEspecializado),
        compEstrategico: snToNumber(registroData.compEstrategico),
        compOncologico: snToNumber(registroData.compOncologico),
        compRara: snToNumber(registroData.compRara),

        usoHospitalar: snToNumber(registroData.usoHospitalar),
        farmaciaPopular: snToNumber(registroData.farmaciaPopular),

        dataRenamePublicacao: formatDate(registroData.dataRenamePublicacao),
        dataRenameAmpliacao: formatDate(registroData.dataRenameAmpliacao),
        dataRenameExclusao: formatDate(registroData.dataRenameExclusao),

        regraValidacao: registroData.regraValidacao,
        observacoes: registroData.observacoes,

        tags: registroData.tags,
        tagsArray: registroData.tagsArray,
      };

      setFormMain(formValues);
      setDataRaw(formValues);
      triggerRefresh();
    } catch (error) {
      console.error('Erro ao obter dados:', error);
    }
  };

  useEffect(() => {
    if (fichaID !== 'undefined') {
      fetchData(fichaID);
    }
  }, [fichaID]);

  // Denominações Genéricas
  const { data: denominacaoGenericaData } = useDenominacoesGenericasCache();
  const tipoProdutoLabel = React.useMemo(() => {
    if (!formMain.denominacaoGenericaId) return '';

    const denominacao = denominacaoGenericaData.find(
      (denom) => denom.id === Number(formMain.denominacaoGenericaId),
    );

    if (!denominacao) return '';

    return (
      tipoProduto.find(
        (option) => Number(option.value) === Number(denominacao.tipoProduto),
      )?.label ?? ''
    );
  }, [formMain.denominacaoGenericaId, denominacaoGenericaData, tipoProduto]);
  const denominacoesGenericasOptions = React.useMemo(
    () => [
      { value: 0, label: 'Não informado' },
      ...denominacaoGenericaData.map((item) => ({
        value: item.id,
        label: item.denominacaoGenerica,
      })),
    ],
    [denominacaoGenericaData],
  );

  // OBM VMP
  const [produtosObmOptions, setProdutosObmOptions] = useState<ProdutoOption[]>(
    [],
  );
  const [obmsOptions, setObmsOptions] = useState<ProdutoOption[]>([]);
  const { data: produtosVmp } = useProdutoVmpCache();
  useEffect(() => {
    if (!produtosVmp.length) return;

    setProdutosObmOptions([
      { value: 0, label: 'Não informado' },
      ...produtosVmp.map((p) => ({
        value: Number(p.id),
        label: p.nome,
      })),
    ]);

    setObmsOptions([
      { value: 0, label: 'Não informado' },
      ...produtosVmp.map((p) => ({
        value: Number(p.id),
        label: p.obm,
      })),
    ]);
  }, [produtosVmp]);

  const subAbas =
    fichaID !== 'undefined'
      ? SubAbasProdutosDaf(fichaID)
      : [
          {
            label: 'Dados Gerais',
            rota: `/daf/produtosdaf/produtos/dadosgerais`,
          },
        ];

  const logs = functionLogData(logData);

  const buildProdutoFarmaceutico = (
    denominacao?: string,
    concentracao?: string,
    formaLabel?: string,
  ) => {
    if (!denominacao && !concentracao && !formaLabel) return '';

    let nome = denominacao ?? '';

    if (concentracao) {
      nome += ` ${concentracao}`;
    }

    if (formaLabel) {
      nome += ` (${formaLabel})`;
    }

    return nome.trim();
  };

  const handleChange = React.useCallback(
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;

      setFormMain((prev) => {
        const updated = {
          ...prev,
          [name]: value,
        };

        const shouldUpdateProduto =
          name === 'denominacaoGenericaId' ||
          name === 'concentracao' ||
          name === 'formaFarmaceutica';

        if (shouldUpdateProduto) {
          const denominacao = denominacaoGenericaData.find(
            (d) => d.id === Number(updated.denominacaoGenericaId),
          )?.denominacaoGenerica;

          const formaFarmaceuticaLabel = formaFarmaceutica.find(
            (f) => String(f.value) === String(updated.formaFarmaceutica),
          )?.label;

          const concentracao = String(updated.concentracao ?? '').trim();

          updated.produtoFarmaceutico = buildProdutoFarmaceutico(
            denominacao,
            concentracao,
            formaFarmaceuticaLabel,
          );
        }

        return updated;
      });
    },
    [denominacaoGenericaData, formaFarmaceutica],
  );

  const camposObrigatoriosBase: Record<string, string> = {
    statusDistribuicao: 'Status SisAF',
    denominacaoGenericaId: 'Denominação Genérica',
    concentracao: 'Concentração/Dose',
    formaFarmaceutica: 'Forma Farmacêutica',
    aware: 'AwARE',
    umidade: 'Umidade do Produto',
    acondicionamento: 'Acondicionamento',
    statusRename: 'Status RENAME',
    codigoAtc: 'ATC',
    compBasico: 'Componente AF - Básico',
    compEspecializado: 'Componente AF - Especializado',
    compEstrategico: 'Componente AF - Estratégico',
    usoHospitalar: 'Uso Hospitalar',
    farmaciaPopular: 'Disponível Farmácia Popular',
  };

  const camposObrigatorios: Record<string, string> = {
    ...camposObrigatoriosBase,

    ...(Number(formMain.compEspecializado) === 1 &&
      Number(formMain.especializadoGrupo) === 0 && {
        especializadoGrupo: 'Grupo de Financiamento Especializado',
      }),

    ...(Number(formMain.compOncologico) === 1 &&
      Number(formMain.oncologicoGrupo) === 0 && {
        oncologicoGrupo: 'Grupo de Financiamento Oncológico',
      }),
  };

  const camposAjustar = {
    number: ['id', 'usuarioId', 'status', 'restricaoComponenteAf'],
    string: ['sigla', 'nomeSetorDaf', 'observacoes'],
    date: ['dataCriacao', 'dataExtincao'],
  };

  const salvar = async () => {
    const payloadApi = produtoDafFormToApi(formMain);

    const ok = await handleSave(
      payloadApi,
      camposObrigatorios,
      camposAjustar,
      'produtodaf',
      'daf/produtosdaf/produtos',
      fetchData,
      true,
      dataRaw ? produtoDafFormToApi(dataRaw) : null,
      clearProdutosDafMemoryCache,
    );
  };

  const deletar = () =>
    handleDelete(
      fichaID,
      'produtodaf',
      'daf/produtosdaf/produtos',
      clearProdutosDafMemoryCache,
    );

  // Hooks de Tags
  const {
    tagsProdutos,
    selectedTags,
    showOffcanvasTags,
    handleTagsChange,
    handleCreateTags,
    handleToggleOffcanvasTags,
  } = useProdutoTags(formMain, setFormMain, fetchData, fichaID);

  //Retorno
  return (
    <>
      <SubAbas>
        <StyledLink
          to="/daf/produtosdaf/produtos"
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <FaArrowLeft />
        </StyledLink>
        <BarraSubAbas subAbas={subAbas} />
      </SubAbas>

      <ContainerConteudo>
        <WorkspaceFicha>
          {fichaID !== 'undefined' && (
            <Log
              {...logs}
              entity="TB_PRODUTO_DAF"
              id={Number(formMain.id)}
              refreshTriggerLog={refreshTriggerLog}
            />
          )}

          {/* Identificação do Produto DAF */}
          <BoxConteudo boxTitulo="Identificação do Produto DAF">
            <WorkspaceFicha>
              <BoxConteudo boxTitulo="Nome do Produto e OBM">
                <CustomRow style={{ marginTop: '-15px' }}>
                  <CustomCol
                    width="250px"
                    helpText="Se o produto está disponível para ser programado e distribuído para Secretarias de Saúde."
                  >
                    <CustomSelect
                      name="statusDistribuicao"
                      options={statusBinAtivoInativo}
                      title="Status SisAF"
                      value={formMain.statusDistribuicao}
                      onChange={handleChange}
                      style={getStatusDistribuicaoStyle(
                        formMain.statusDistribuicao,
                      )}
                      required
                    />
                  </CustomCol>
                  <CustomCol
                    helpTextWidth={'300px'}
                    helpText="Não é possível digitar. O nome do produto é gerado automaticamente a partir da Denominação Genérica, Concentração/Dose e Forma Farmacêutica."
                  >
                    <CustomInputText
                      name="produtoFarmaceutico"
                      value={formMain.produtoFarmaceutico}
                      title="Produto Farmacêutico - DAF"
                      // onChange={handleChange}

                      readOnly
                    />
                  </CustomCol>
                </CustomRow>

                {/* OBM */}
                <CustomRow>
                  <CustomCol
                    width="250px"
                    helpText="Se o produto está disponível para ser programado e distribuído para Secretarias de Saúde."
                  >
                    <CustomSelect
                      name="obmVmpId"
                      options={obmsOptions}
                      title="Código OBM"
                      value={formMain.obmVmpId}
                      onChange={handleChange}
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomSelect
                      name="obmVmpId"
                      options={produtosObmOptions}
                      title="Produto Farmacêutico - OBM"
                      value={formMain.obmVmpId}
                      onChange={handleChange}
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              <BoxConteudo boxTitulo="Classificações do Produto">
                <CustomRow style={{ marginTop: '-15px' }}>
                  <CustomCol width="155px">
                    <CustomInputText
                      name="tipoProduto"
                      value={tipoProdutoLabel || ''}
                      title="Tipo de Produto"
                      readOnly
                    />
                  </CustomCol>

                  <CustomCol>
                    <CustomSelect
                      name="denominacaoGenericaId"
                      options={denominacoesGenericasOptions}
                      title="Denominação Genérica"
                      value={formMain.denominacaoGenericaId || 'Não informado'}
                      onChange={handleChange}
                      required
                    />
                  </CustomCol>

                  <CustomCol>
                    <CustomInputText
                      name="concentracao"
                      value={formMain.concentracao}
                      title="Concentração/Dose"
                      onChange={handleChange}
                      required
                    />
                  </CustomCol>
                </CustomRow>

                {/* Forma Farmacêutica, AwARE, Umidade, Acondicionamento e Flags */}
                <CustomRow>
                  <CustomCol>
                    <CustomSelect
                      name="formaFarmaceutica"
                      options={formaFarmaceutica}
                      title="Forma Farmacêutica"
                      value={formMain.formaFarmaceutica}
                      onChange={handleChange}
                      required
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomSelect
                      name="aware"
                      options={classificacaoAware}
                      title="AwARE"
                      value={formMain.aware}
                      onChange={handleChange}
                      required
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomSelect
                      name="umidade"
                      options={umidade}
                      title="Umidade do Produto"
                      value={formMain.umidade}
                      onChange={handleChange}
                      required
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomSelect
                      name="acondicionamento"
                      options={acondicionamento}
                      title="Acondicionamento"
                      value={formMain.acondicionamento}
                      onChange={handleChange}
                      required
                    />
                  </CustomCol>
                  {/* <CustomCol width="8%">
                    <CustomSelect
                      name="oncologico"
                      options={statusBin}
                      title="Oncológico"
                      value={formMain.oncologico}
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.oncologico)}
                    />
                  </CustomCol>
                  <CustomCol width="8%">
                    <CustomSelect
                      name="biologico"
                      options={statusBin}
                      title="Biológico"
                      value={formMain.biologico}
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.biologico)}
                    />
                  </CustomCol> */}
                  <CustomCol width="150px">
                    <CustomSelect
                      name="doencaRara"
                      options={statusBin}
                      title="Doença Rara"
                      value={formMain.doencaRara}
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.doencaRara)}
                    />
                  </CustomCol>
                  <CustomCol width="150px">
                    <CustomSelect
                      name="usoHospitalar"
                      options={statusBin}
                      title="Uso Hospitalar?"
                      value={formMain.usoHospitalar}
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.usoHospitalar)}
                      required
                    />
                  </CustomCol>
                  <CustomCol width="190px">
                    <CustomSelect
                      name="farmaciaPopular"
                      options={statusBin}
                      title="Disp. Farmácia Popular?"
                      value={formMain.farmaciaPopular}
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.farmaciaPopular)}
                      required
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              <BoxConteudo boxTitulo="Status RENAME e ATC">
                <CustomRow style={{ marginTop: '-15px' }}>
                  <CustomCol width="268px">
                    <CustomSelect
                      name="statusRename"
                      options={statusRename}
                      title="Status RENAME"
                      value={formMain.statusRename}
                      onChange={handleChange}
                      style={getStatusRenameStyle(formMain.statusRename)}
                      required
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomInputDate
                      name="dataRenamePublicacao"
                      title="Data de Publicação"
                      value={formMain.dataRenamePublicacao}
                      onChange={handleChange}
                      readOnly={
                        Number(formMain.statusRename) == 2 ||
                        Number(formMain.statusRename) == 3
                          ? false
                          : true
                      }
                      required={
                        Number(formMain.statusRename) == 2 ||
                        Number(formMain.statusRename) == 3
                          ? true
                          : false
                      }
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomInputDate
                      name="dataRenameExclusao"
                      title="Data de Exclusão"
                      value={formMain.dataRenameExclusao}
                      onChange={handleChange}
                      readOnly={
                        Number(formMain.statusRename) == 3 ? false : true
                      }
                      required={
                        Number(formMain.statusRename) == 3 ? true : false
                      }
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomInputText
                      name="codigoAtc"
                      value={
                        formMain.codigoAtc === null
                          ? 'Não informado'
                          : formMain.codigoAtc
                      }
                      title="ATC"
                      onChange={handleChange}
                      required
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              <BoxConteudo boxTitulo="Componentes da Assistência Farmacêutica">
                <CustomRow style={{ marginTop: '-15px' }}>
                  <CustomCol>
                    <CustomSelect
                      name="compBasico"
                      value={formMain.compBasico}
                      options={statusBin}
                      title="Básico"
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.compBasico)}
                      required
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomSelect
                      name="compEspecializado"
                      value={formMain.compEspecializado}
                      options={statusBin}
                      title="Especializado"
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.compEspecializado)}
                      required
                    />
                  </CustomCol>
                  <CustomCol width="250px">
                    <CustomSelect
                      name="especializadoGrupo"
                      options={grupoFinanciamento}
                      title="Grupo Finan. Especializado"
                      onChange={handleChange}
                      disabled={Number(formMain.compEspecializado) !== 1}
                      value={
                        Number(formMain.compEspecializado) === 1
                          ? formMain.especializadoGrupo
                          : ''
                      }
                      required={Number(formMain.compEspecializado) === 1}
                    />
                  </CustomCol>

                  <CustomCol>
                    <CustomSelect
                      name="compEstrategico"
                      value={formMain.compEstrategico}
                      options={statusBin}
                      title="Estratégico"
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.compEstrategico)}
                      required
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomSelect
                      name="compOncologico"
                      value={formMain.compOncologico}
                      options={statusBin}
                      title="Oncológico"
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.compOncologico)}
                      required
                    />
                  </CustomCol>
                  <CustomCol width="250px">
                    <CustomSelect
                      name="oncologicoGrupo"
                      options={grupoOncologico}
                      title="Grupo Finan. Oncológico"
                      onChange={handleChange}
                      disabled={Number(formMain.compOncologico) !== 1}
                      value={
                        Number(formMain.compOncologico) === 1
                          ? formMain.oncologicoGrupo
                          : ''
                      }
                      required={Number(formMain.compOncologico) === 1}
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomSelect
                      name="compRara"
                      value={formMain.compRara}
                      options={statusBin}
                      title="Raras"
                      onChange={handleChange}
                      style={getStatusBinStyle(formMain.compRara)}
                      required
                    />
                  </CustomCol>

                  {/* <CustomCol>
              <CustomSelect
                name="possuiPDP"
                value={formMain.}
                id="possuiPDP"
                label="Possui PDP?"
                onChange={handleChange}
                style={statusPossuiPdp}
                readonly
              />
            </CustomCol> */}
                </CustomRow>
              </BoxConteudo>

              {/* Tags do Produto */}
              {fichaID !== 'undefined' && (
                <BoxConteudo boxTitulo="Tags do Produto">
                  <CustomRow style={{ marginTop: '-15px' }}>
                    <CustomCol width="100%">
                      <ReadonlyInputWithEdit
                        id="tags"
                        label=""
                        value={formMain.tags || 'Não informado'}
                        onEdit={handleToggleOffcanvasTags}
                        multiline
                        rows={2}
                      />
                    </CustomCol>
                  </CustomRow>
                  <CustomCol>
                    <OffcanvasTagsProduto
                      show={showOffcanvasTags}
                      onClose={handleToggleOffcanvasTags}
                      tagsProdutos={tagsProdutos}
                      selectedTags={selectedTags}
                      onTagsChange={handleTagsChange}
                      onSave={handleCreateTags}
                      width="80vw"
                    />
                  </CustomCol>
                </BoxConteudo>
              )}
            </WorkspaceFicha>
          </BoxConteudo>

          {/* Incorporações */}

          {fichaID !== 'undefined' && (
            <>
              <ProdutoDafIncorporacoes
                produtoId={fichaID}
                obmId={formMain.obmVmpId ? String(formMain.obmVmpId) : ''}
              />

              <ProdutoKits produtoId={Number(fichaID)} />

              <ProdutoOutrosSistemas produtoId={fichaID} userId={userID} />
            </>
          )}
          {/* Critério de Programação */}
          <BoxConteudo
            boxTitulo="Critério de Programação"
            helpText="Diz respeito aos critérios adotados para avaliar a programação e distribuição do produto para Secretarias Estaduais de Saúde."
            fechado
          >
            <CustomRow style={{ marginTop: '-15px' }}>
              <CustomCol
                width="200px"
                helpText="Se requer lista de pacientes na programação do produto."
              >
                <CustomSelect
                  name="listaPaciente"
                  options={listaPaciente}
                  title="Lista de Pacientes"
                  value={formMain.listaPaciente}
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol width="100%">
                <CustomInputText
                  name="regraValidacao"
                  value={formMain.regraValidacao}
                  title="Regras de validação"
                  type="textArea"
                  onChange={handleChange}
                  rows={10}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>

          {/* Observações Gerais */}
          <BoxConteudo boxTitulo="Observações Gerais" fechado>
            <CustomRow style={{ marginTop: '-15px' }}>
              <CustomCol width="100%">
                <CustomInputText
                  name="observacoes"
                  value={formMain.observacoes}
                  title=""
                  type="textArea"
                  onChange={handleChange}
                  rows={5}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
        </WorkspaceFicha>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        {fichaID !== 'undefined' ? (
          <WorkspaceBotoes
            delete
            onClickDelete={deletar}
            deleteDisabled={!cudProdutoDaf}
            save
            onClickSave={salvar}
            saveDisabled={!cudProdutoDaf}
          />
        ) : (
          <WorkspaceBotoes
            save
            onClickSave={salvar}
            saveDisabled={!cudProdutoDaf}
          />
        )}
      </BarraInferiorBotoes>
    </>
  );
}
